import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { JobSheetTab } from "@/pages/team/JobSheetTab";
import { FirstLoginWizard } from "@/pages/team/FirstLoginWizard";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { 
  FileSpreadsheet, Download, Lock, Loader2, ArrowLeft, CheckCircle2,
  Upload, FileText, Award, Shield, Calendar, User, Mail, AlertCircle
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import axios from "axios";
import { CleanKinLogo } from "@/components/Layout";
import { logError } from "@/lib/log";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const dbsStatusMessage = (status) => {
  if (status === "verified") return "Your DBS certificate has been verified.";
  if (status === "pending") return "Your DBS certificate is pending review by the admin team.";
  return "Your DBS certificate needs to be renewed.";
};

const CleanerPortal = () => {
  const [accessCode, setAccessCode] = useState("");
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [signInMode, setSignInMode] = useState("account"); // "account" | "code"
  const [me, setMe] = useState(null); // { kind, name, email, must_change_password }
  const [changePwOpen, setChangePwOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [changingPw, setChangingPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [worksheetUrl, setWorksheetUrl] = useState(null);
  const [activeTab, setActiveTab] = useState("worksheet");
  
  // DBS Upload state
  const [dbsForm, setDbsForm] = useState({
    cleaner_name: "",
    cleaner_email: "",
    dbs_certificate_number: "",
    dbs_issue_date: ""
  });
  const [dbsFile, setDbsFile] = useState(null);
  const [dbsStatus, setDbsStatus] = useState(null);
  const [uploadingDbs, setUploadingDbs] = useState(false);
  const fileInputRef = useRef(null);
  
  // Partnership Certificate state
  const [certForm, setCertForm] = useState({
    cleaner_name: "",
    cleaner_email: "",
    confirm_self_employed: false,
    confirm_tax_responsibility: false
  });
  const [certificate, setCertificate] = useState(null);
  const [certificateText, setCertificateText] = useState(null);
  const [generatingCert, setGeneratingCert] = useState(false);

  const finishLogin = async () => {
    setAuthenticated(true);
    // Fetch data in parallel — cookies sent automatically
    await Promise.all([
      fetchWorksheet(),
      fetchDbsStatus(),
      fetchCertificate(),
      fetchMe(),
    ]);
  };

  const fetchMe = async () => {
    try {
      const { data } = await axios.get(`${API}/cleaner/me`);
      setMe(data);
      if (data?.kind === "account" && data.must_change_password) {
        setWizardOpen(true);
      }
    } catch {
      // Not fatal — shared-code sessions won't always resolve to an account
    }
  };

  // Auto-resume: if a valid cleaner cookie exists from a previous visit,
  // skip the login screen entirely.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/cleaner/me`);
        if (data) {
          setMe(data);
          await finishLogin();
        }
      } catch {
        /* not signed in — show login form */
      }
    })();
  }, []);

  const handleAccountSignIn = async (e) => {
    e.preventDefault();
    if (!loginEmail.trim() || !loginPassword) {
      toast.error("Enter your email and password");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/cleaner/login`, {
        email: loginEmail.trim(),
        password: loginPassword,
      });
      toast.success("Signed in");
      await finishLogin();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();

    if (!accessCode.trim()) {
      toast.error("Please enter your access code");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/cleaner/verify`, { code: accessCode.trim() });
      toast.success("Access granted!");
      await finishLogin();
    } catch (error) {
      toast.error("Invalid access code");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.new_password.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (pwForm.new_password !== pwForm.confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setChangingPw(true);
    try {
      await axios.post(`${API}/cleaner/change-password`, {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      });
      toast.success("Password updated");
      setChangePwOpen(false);
      setPwForm({ current_password: "", new_password: "", confirm: "" });
      await fetchMe();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Password change failed");
    } finally {
      setChangingPw(false);
    }
  };

  const fetchWorksheet = async () => {
    try {
      const worksheetRes = await axios.get(`${API}/cleaner/worksheet`);
      setWorksheetUrl(worksheetRes.data.worksheet_url);
    } catch (error) {
      logError("Error fetching worksheet:", error);
    }
  };

  const fetchDbsStatus = async () => {
    try {
      const res = await axios.get(`${API}/cleaner/dbs-status`);
      setDbsStatus(res.data);
      if (res.data) {
        setDbsForm({
          cleaner_name: res.data.cleaner_name || "",
          cleaner_email: res.data.cleaner_email || "",
          dbs_certificate_number: res.data.dbs_certificate_number || "",
          dbs_issue_date: res.data.dbs_issue_date || ""
        });
      }
    } catch (error) {
      logError("Error fetching DBS status:", error);
    }
  };

  const fetchCertificate = async () => {
    try {
      const res = await axios.get(`${API}/cleaner/partnership-certificate`);
      if (res.data) {
        setCertificate(res.data);
        setCertForm({
          cleaner_name: res.data.cleaner_name || "",
          cleaner_email: res.data.cleaner_email || "",
          confirm_self_employed: true,
          confirm_tax_responsibility: true
        });
        const textRes = await axios.get(`${API}/cleaner/partnership-certificate/${res.data.id}/text`);
        setCertificateText(textRes.data.certificate_text);
      }
    } catch (error) {
      logError("Error fetching certificate:", error);
    }
  };

  const handleDownload = () => {
    if (worksheetUrl) {
      const link = document.createElement('a');
      link.href = worksheetUrl;
      link.download = 'Clean_Kin_Client_Worksheet.xlsx';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Download started!");
    }
  };

  const handleDbsFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        toast.error("Only PDF files are accepted");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size must be less than 5MB");
        return;
      }
      setDbsFile(file);
    }
  };

  const handleDbsUpload = async (e) => {
    e.preventDefault();
    
    if (!dbsForm.cleaner_name || !dbsForm.cleaner_email || !dbsForm.dbs_certificate_number || !dbsForm.dbs_issue_date) {
      toast.error("Please fill in all fields");
      return;
    }
    if (!dbsFile) {
      toast.error("Please select a PDF file");
      return;
    }

    setUploadingDbs(true);
    try {
      const formData = new FormData();
      formData.append('cleaner_name', dbsForm.cleaner_name);
      formData.append('cleaner_email', dbsForm.cleaner_email);
      formData.append('dbs_certificate_number', dbsForm.dbs_certificate_number);
      formData.append('dbs_issue_date', dbsForm.dbs_issue_date);
      formData.append('dbs_file', dbsFile);

      await axios.post(`${API}/cleaner/dbs-upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success("DBS certificate uploaded successfully!");
      setDbsFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await fetchDbsStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload DBS certificate");
    } finally {
      setUploadingDbs(false);
    }
  };

  const handleGenerateCertificate = async (e) => {
    e.preventDefault();
    
    if (!certForm.cleaner_name || !certForm.cleaner_email) {
      toast.error("Please fill in all fields");
      return;
    }
    if (!certForm.confirm_self_employed || !certForm.confirm_tax_responsibility) {
      toast.error("You must confirm both declarations");
      return;
    }

    setGeneratingCert(true);
    try {
      const res = await axios.post(`${API}/cleaner/partnership-certificate`, certForm);
      
      setCertificate(res.data);
      toast.success("Partnership certificate generated!");
      
      // Fetch certificate text
      const textRes = await axios.get(`${API}/cleaner/partnership-certificate/${res.data.id}/text`);
      setCertificateText(textRes.data.certificate_text);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate certificate");
    } finally {
      setGeneratingCert(false);
    }
  };

  const handlePrintCertificate = () => {
    if (!certificateText) return;
    
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error("Please allow popups to print the certificate");
      return;
    }
    
    // Build the print document with safe DOM APIs (no document.write / innerHTML)
    const doc = printWindow.document;
    doc.title = "Partnership Certificate - Clean Kin";

    const style = doc.createElement("style");
    style.textContent =
      "body { font-family: 'Georgia', serif; max-width: 800px; margin: 40px auto; padding: 40px; line-height: 1.6; }" +
      "pre { white-space: pre-wrap; font-family: 'Georgia', serif; }";
    doc.head.appendChild(style);

    const pre = doc.createElement("pre");
    pre.textContent = certificateText; // textContent escapes safely
    doc.body.appendChild(pre);

    printWindow.print();
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-amber-100 text-amber-700",
      verified: "bg-green-100 text-green-700",
      expired: "bg-red-100 text-red-700",
      rejected: "bg-red-100 text-red-700",
      active: "bg-green-100 text-green-700"
    };
    return <Badge className={`${styles[status] || "bg-gray-100 text-gray-600"} font-medium`}>{status}</Badge>;
  };

  return (
    <div className="min-h-screen bg-[#F9FAF9] flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-2xl"
      >
        <div className="text-center mb-8">
          <Link to="/">
            <CleanKinLogo size="default" />
          </Link>
          <h1 className="text-2xl font-bold text-[#1A2E12] mt-4">Cleaner Portal</h1>
          <p className="text-[#5C6B57] mt-2">Access resources for Clean Kin team members</p>
        </div>

        <Card className="bg-white border-[#E2E8E0] rounded-2xl shadow-natural">
          <CardContent className="pt-6">
            {!authenticated ? (
              <div className="space-y-6">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-[#E8F5E9] flex items-center justify-center mx-auto mb-4">
                    <Lock className="w-8 h-8 text-[#2D5016]" strokeWidth={1.5} />
                  </div>
                  <p className="text-[#5C6B57] text-sm">
                    Sign in to reach your worksheet, DBS records and job sheets.
                  </p>
                </div>

                <Tabs value={signInMode} onValueChange={setSignInMode} className="w-full">
                  <TabsList className="grid grid-cols-2 mb-4" data-testid="sign-in-mode-tabs">
                    <TabsTrigger value="account" data-testid="sign-in-tab-account">
                      <User className="w-4 h-4 mr-1" /> My account
                    </TabsTrigger>
                    <TabsTrigger value="code" data-testid="sign-in-tab-code">
                      <Lock className="w-4 h-4 mr-1" /> Access code
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="account">
                    <form onSubmit={handleAccountSignIn} className="space-y-4" data-testid="cleaner-account-form">
                      <div>
                        <Label className="text-sm font-semibold text-[#2D5016]">Email</Label>
                        <Input
                          type="email"
                          placeholder="you@example.com"
                          value={loginEmail}
                          onChange={(e) => setLoginEmail(e.target.value)}
                          className="mt-1 h-11"
                          autoComplete="email"
                          required
                          data-testid="cleaner-login-email"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold text-[#2D5016]">Password</Label>
                        <Input
                          type="password"
                          placeholder="Your password"
                          value={loginPassword}
                          onChange={(e) => setLoginPassword(e.target.value)}
                          className="mt-1 h-11"
                          autoComplete="current-password"
                          required
                          data-testid="cleaner-login-password"
                        />
                      </div>
                      <Button
                        type="submit"
                        className="w-full bg-[#2D5016] hover:bg-[#1A2E12] text-white rounded-full py-6"
                        disabled={loading}
                        data-testid="cleaner-signin-btn"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                            Signing in…
                          </>
                        ) : (
                          "Sign in"
                        )}
                      </Button>
                      <p className="text-xs text-center text-[#5C6B57]">
                        Not received an invite? Ask the office to add you.
                      </p>
                      <div className="text-center">
                        <Link
                          to="/team/forgot-password"
                          className="text-xs text-[#2D5016] hover:underline"
                          data-testid="cleaner-forgot-password-link"
                        >
                          Forgot your password?
                        </Link>
                      </div>
                    </form>
                  </TabsContent>

                  <TabsContent value="code">
                    <form onSubmit={handleVerify} className="space-y-4">
                      <div>
                        <Label className="text-sm font-semibold text-[#2D5016]">Team access code</Label>
                        <Input
                          type="password"
                          placeholder="Enter your access code"
                          value={accessCode}
                          onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                          className="mt-1 h-12 text-center tracking-widest"
                          required
                          data-testid="cleaner-access-code"
                        />
                      </div>
                      <Button
                        type="submit"
                        className="w-full bg-[#2D5016] hover:bg-[#1A2E12] text-white rounded-full py-6"
                        disabled={loading}
                        data-testid="cleaner-verify-btn"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                            Verifying...
                          </>
                        ) : (
                          "Access Portal"
                        )}
                      </Button>
                      <p className="text-xs text-center text-[#5C6B57]">
                        Legacy fallback — will be retired once every cleaner has a personal account.
                      </p>
                    </form>
                  </TabsContent>
                </Tabs>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="text-center mb-2">
                  <div className="w-16 h-16 rounded-full bg-[#E8F5E9] flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-8 h-8 text-[#2D5016]" strokeWidth={1.5} />
                  </div>
                  <h2 className="text-xl font-semibold text-[#1A2E12]">
                    {me?.kind === "account" && me?.name ? `Welcome back, ${me.name}` : "Welcome to the Team Portal"}
                  </h2>
                  {me?.kind === "account" && (
                    <p className="text-xs text-[#5C6B57] mt-1" data-testid="cleaner-me-email">
                      Signed in as {me.email}
                    </p>
                  )}
                </div>

                {me?.kind === "account" && me?.must_change_password && (
                  <div
                    className="bg-[#FFF4D6] border border-[#F0D77A] rounded-xl p-4 flex items-start gap-3"
                    data-testid="temp-password-banner"
                  >
                    <AlertCircle className="w-5 h-5 text-[#7C5A00] mt-0.5 shrink-0" />
                    <div className="flex-1 text-sm">
                      <p className="font-semibold text-[#1A2E12]">Set a permanent password</p>
                      <p className="text-[#5C6B57]">
                        You&apos;re signed in with a temporary password. Please replace it now.
                      </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => setWizardOpen(true)}
                      className="bg-[#2D5016] hover:bg-[#1A2E12] text-white rounded-full"
                      data-testid="open-change-password"
                    >
                      Change now
                    </Button>
                  </div>
                )}

                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <TabsList className="grid grid-cols-4 mb-6">
                    <TabsTrigger value="worksheet" className="flex items-center gap-1 text-xs sm:text-sm">
                      <FileSpreadsheet className="w-4 h-4" />
                      <span className="hidden sm:inline">Worksheet</span>
                    </TabsTrigger>
                    <TabsTrigger value="training" className="flex items-center gap-1 text-xs sm:text-sm" data-testid="tab-training">
                      <Award className="w-4 h-4" />
                      <span className="hidden sm:inline">Training</span>
                    </TabsTrigger>
                    <TabsTrigger value="dbs" className="flex items-center gap-1 text-xs sm:text-sm">
                      <Shield className="w-4 h-4" />
                      <span className="hidden sm:inline">DBS</span>
                    </TabsTrigger>
                    <TabsTrigger value="certificate" className="flex items-center gap-1 text-xs sm:text-sm">
                      <Award className="w-4 h-4" />
                      <span className="hidden sm:inline">Certificate</span>
                    </TabsTrigger>
                  </TabsList>

                  {/* Training Snapshot Tab */}
                  <TabsContent value="training" data-testid="training-snapshot">
                    <div className="space-y-4">
                      <div className="bg-[#E8F5E9] rounded-xl p-6 border border-[#7CB342]/30">
                        <h3 className="text-lg font-semibold text-[#1A2E12] mb-2">
                          Clean Kin — Standards Snapshot
                        </h3>
                        <p className="text-sm text-[#5C6B57] mb-4">
                          The standards every Clean Kin team member is expected to uphold on every visit. Read it before each shift.
                        </p>
                      </div>

                      <div className="bg-white rounded-xl p-5 border border-[#E2E8E0]">
                        <h4 className="font-semibold text-[#1A2E12] mb-2 flex items-center gap-2">
                          <span className="w-7 h-7 rounded-full bg-[#2D5016] text-white text-sm flex items-center justify-center">1</span>
                          Appearance
                        </h4>
                        <ul className="list-disc list-inside text-sm text-[#5C6B57] space-y-1 ml-1">
                          <li>Neat and tidy at all times — clean clothing or branded uniform if issued.</li>
                          <li>Hair tied back. Minimal jewellery. Closed-toe, non-marking footwear.</li>
                          <li>Carry Clean Kin ID. Always introduce yourself by name on arrival.</li>
                        </ul>
                      </div>

                      <div className="bg-white rounded-xl p-5 border border-[#E2E8E0]">
                        <h4 className="font-semibold text-[#1A2E12] mb-2 flex items-center gap-2">
                          <span className="w-7 h-7 rounded-full bg-[#2D5016] text-white text-sm flex items-center justify-center">2</span>
                          Punctuality — the 5-minute rule
                        </h4>
                        <ul className="list-disc list-inside text-sm text-[#5C6B57] space-y-1 ml-1">
                          <li>Aim to arrive at the start of the booked time window.</li>
                          <li>If you will be more than <strong>5 minutes</strong> late, message the customer AND your team lead before the deadline passes.</li>
                          <li>Repeated lateness without notice is reviewed at the end of each month — fair policy, three warnings.</li>
                        </ul>
                      </div>

                      <div className="bg-white rounded-xl p-5 border border-[#E2E8E0]">
                        <h4 className="font-semibold text-[#1A2E12] mb-2 flex items-center gap-2">
                          <span className="w-7 h-7 rounded-full bg-[#2D5016] text-white text-sm flex items-center justify-center">3</span>
                          Raise an alert
                        </h4>
                        <p className="text-sm text-[#5C6B57] mb-2">
                          Use the alert channel if any of the following happen — your safety and the customer&apos;s trust matter more than finishing the job:
                        </p>
                        <ul className="list-disc list-inside text-sm text-[#5C6B57] space-y-1 ml-1">
                          <li>You feel unsafe on site (aggressive behaviour, hazards, intoxicated occupant).</li>
                          <li>You spot pre-existing damage or breakage — photograph it before starting.</li>
                          <li>You are in dispute with a customer over scope, payment or access.</li>
                          <li>Equipment failure or product allergy/sensitivity concern.</li>
                        </ul>
                        <p className="text-xs text-[#5C6B57] mt-3">
                          Email <strong>info@cleankinservices.co.uk</strong> with subject <em>ALERT — [your name]</em>. We respond within 30 minutes during working hours.
                        </p>
                      </div>

                      <div className="bg-[#F9FAF9] rounded-xl p-5 border border-[#E2E8E0]">
                        <h4 className="font-semibold text-[#1A2E12] mb-2">Service standard checklist</h4>
                        <p className="text-sm text-[#5C6B57] mb-2">
                          Brief reminder — the customer-facing standard you&apos;re delivering against:
                        </p>
                        <ul className="text-sm text-[#5C6B57] space-y-1">
                          <li>✓ Surfaces wiped, kitchen and bathroom degreased, floors finished.</li>
                          <li>✓ High-touch points sanitised (handles, switches, taps).</li>
                          <li>✓ Skirting and edges checked on Deep Cleans.</li>
                          <li>✓ All products and equipment back in place. Bins emptied. Final walk-through.</li>
                        </ul>
                      </div>
                    </div>
                  </TabsContent>

                  {/* Job Sheet (interactive cleaner checklist) */}
                  <TabsContent value="worksheet">
                    <JobSheetTab accessCode={accessCode} cleanerName={me?.kind === "account" ? me?.name : ""} />
                  </TabsContent>

                  {/* DBS Upload Tab */}
                  <TabsContent value="dbs">
                    {dbsStatus ? (
                      <div className="space-y-4">
                        <div className="bg-[#E8F5E9] rounded-xl p-6">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <Shield className="w-8 h-8 text-[#2D5016]" />
                              <div>
                                <h3 className="font-semibold text-[#1A2E12]">DBS Certificate</h3>
                                <p className="text-sm text-[#5C6B57]">Uploaded {new Date(dbsStatus.uploaded_at).toLocaleDateString('en-GB')}</p>
                              </div>
                            </div>
                            {getStatusBadge(dbsStatus.status)}
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-[#5C6B57]">Certificate Number</p>
                              <p className="font-medium text-[#1A2E12]">{dbsStatus.dbs_certificate_number}</p>
                            </div>
                            <div>
                              <p className="text-[#5C6B57]">Issue Date</p>
                              <p className="font-medium text-[#1A2E12]">{dbsStatus.dbs_issue_date}</p>
                            </div>
                            <div>
                              <p className="text-[#5C6B57]">Name</p>
                              <p className="font-medium text-[#1A2E12]">{dbsStatus.cleaner_name}</p>
                            </div>
                            <div>
                              <p className="text-[#5C6B57]">File</p>
                              <p className="font-medium text-[#1A2E12]">{dbsStatus.dbs_filename}</p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="bg-[#F9FAF9] rounded-xl p-4">
                          <p className="text-sm text-[#5C6B57]">
                            {dbsStatusMessage(dbsStatus.status)}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <form onSubmit={handleDbsUpload} className="space-y-4">
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                          <div className="flex gap-3">
                            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-sm font-medium text-amber-800">DBS Certificate Required</p>
                              <p className="text-sm text-amber-700">
                                All Clean Kin partners must have a valid DBS certificate on file.
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-sm font-semibold text-[#2D5016]">Full Name *</Label>
                            <Input
                              value={dbsForm.cleaner_name}
                              onChange={(e) => setDbsForm(prev => ({ ...prev, cleaner_name: e.target.value }))}
                              placeholder="John Smith"
                              className="mt-1"
                              data-testid="dbs-name"
                            />
                          </div>
                          <div>
                            <Label className="text-sm font-semibold text-[#2D5016]">Email *</Label>
                            <Input
                              type="email"
                              value={dbsForm.cleaner_email}
                              onChange={(e) => setDbsForm(prev => ({ ...prev, cleaner_email: e.target.value }))}
                              placeholder="john@example.com"
                              className="mt-1"
                              data-testid="dbs-email"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-sm font-semibold text-[#2D5016]">DBS Certificate Number *</Label>
                            <Input
                              value={dbsForm.dbs_certificate_number}
                              onChange={(e) => setDbsForm(prev => ({ ...prev, dbs_certificate_number: e.target.value }))}
                              placeholder="001234567890"
                              className="mt-1"
                              data-testid="dbs-number"
                            />
                          </div>
                          <div>
                            <Label className="text-sm font-semibold text-[#2D5016]">Issue Date *</Label>
                            <Input
                              type="date"
                              value={dbsForm.dbs_issue_date}
                              onChange={(e) => setDbsForm(prev => ({ ...prev, dbs_issue_date: e.target.value }))}
                              className="mt-1"
                              data-testid="dbs-date"
                            />
                          </div>
                        </div>

                        <div>
                          <Label className="text-sm font-semibold text-[#2D5016]">Upload DBS Certificate (PDF) *</Label>
                          <div className="mt-1 border-2 border-dashed border-[#E2E8E0] rounded-xl p-6 text-center hover:border-[#7CB342] transition-colors">
                            <input
                              type="file"
                              accept=".pdf"
                              onChange={handleDbsFileChange}
                              ref={fileInputRef}
                              className="hidden"
                              id="dbs-file"
                              data-testid="dbs-file-input"
                            />
                            <label htmlFor="dbs-file" className="cursor-pointer">
                              {dbsFile ? (
                                <div className="flex items-center justify-center gap-2">
                                  <FileText className="w-6 h-6 text-[#2D5016]" />
                                  <span className="text-[#1A2E12] font-medium">{dbsFile.name}</span>
                                </div>
                              ) : (
                                <>
                                  <Upload className="w-8 h-8 text-[#5C6B57] mx-auto mb-2" />
                                  <p className="text-sm text-[#5C6B57]">Click to upload PDF (max 5MB)</p>
                                </>
                              )}
                            </label>
                          </div>
                        </div>

                        <Button
                          type="submit"
                          className="w-full bg-[#2D5016] hover:bg-[#1A2E12] text-white rounded-full py-5"
                          disabled={uploadingDbs}
                          data-testid="dbs-submit-btn"
                        >
                          {uploadingDbs ? (
                            <>
                              <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Upload className="mr-2 w-4 h-4" />
                              Upload DBS Certificate
                            </>
                          )}
                        </Button>
                      </form>
                    )}
                  </TabsContent>

                  {/* Partnership Certificate Tab */}
                  <TabsContent value="certificate">
                    {certificate ? (
                      <div className="space-y-4">
                        <div className="bg-[#E8F5E9] rounded-xl p-6">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <Award className="w-8 h-8 text-[#2D5016]" />
                              <div>
                                <h3 className="font-semibold text-[#1A2E12]">Partnership Certificate</h3>
                                <p className="text-sm text-[#5C6B57]">{certificate.certificate_number}</p>
                              </div>
                            </div>
                            {getStatusBadge(certificate.status)}
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                            <div>
                              <p className="text-[#5C6B57]">Partner Name</p>
                              <p className="font-medium text-[#1A2E12]">{certificate.cleaner_name}</p>
                            </div>
                            <div>
                              <p className="text-[#5C6B57]">Email</p>
                              <p className="font-medium text-[#1A2E12]">{certificate.cleaner_email}</p>
                            </div>
                            <div>
                              <p className="text-[#5C6B57]">Issue Date</p>
                              <p className="font-medium text-[#1A2E12]">{certificate.issue_date}</p>
                            </div>
                            <div>
                              <p className="text-[#5C6B57]">Valid Until</p>
                              <p className="font-medium text-[#1A2E12]">{certificate.expiry_date}</p>
                            </div>
                          </div>

                          <Button
                            onClick={handlePrintCertificate}
                            className="w-full bg-[#2D5016] hover:bg-[#1A2E12] text-white rounded-full"
                            data-testid="print-certificate-btn"
                          >
                            <Download className="mr-2 w-4 h-4" />
                            Print / Download Certificate
                          </Button>
                        </div>

                        {certificateText && (
                          <div className="bg-[#F9FAF9] rounded-xl p-4">
                            <h4 className="font-semibold text-[#1A2E12] text-sm mb-2">Certificate Preview</h4>
                            <pre className="text-xs text-[#5C6B57] whitespace-pre-wrap font-mono max-h-48 overflow-y-auto">
                              {certificateText}
                            </pre>
                          </div>
                        )}
                      </div>
                    ) : (
                      <form onSubmit={handleGenerateCertificate} className="space-y-4">
                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
                          <div className="flex gap-3">
                            <Award className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-sm font-medium text-blue-800">Partnership Certificate</p>
                              <p className="text-sm text-blue-700">
                                Generate your official partnership certificate confirming your status as a self-employed cleaner.
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-sm font-semibold text-[#2D5016]">Full Name *</Label>
                            <Input
                              value={certForm.cleaner_name}
                              onChange={(e) => setCertForm(prev => ({ ...prev, cleaner_name: e.target.value }))}
                              placeholder="John Smith"
                              className="mt-1"
                              data-testid="cert-name"
                            />
                          </div>
                          <div>
                            <Label className="text-sm font-semibold text-[#2D5016]">Email *</Label>
                            <Input
                              type="email"
                              value={certForm.cleaner_email}
                              onChange={(e) => setCertForm(prev => ({ ...prev, cleaner_email: e.target.value }))}
                              placeholder="john@example.com"
                              className="mt-1"
                              data-testid="cert-email"
                            />
                          </div>
                        </div>

                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                          <h4 className="font-semibold text-amber-800 text-sm mb-3">Self-Employment Declaration</h4>
                          
                          <div className="space-y-3">
                            <div className="flex items-start space-x-3">
                              <Checkbox
                                id="self-employed"
                                checked={certForm.confirm_self_employed}
                                onCheckedChange={(checked) => 
                                  setCertForm(prev => ({ ...prev, confirm_self_employed: checked }))
                                }
                                data-testid="cert-self-employed"
                              />
                              <label htmlFor="self-employed" className="text-sm text-amber-900 leading-tight cursor-pointer">
                                I confirm that I am operating as a self-employed independent contractor and NOT as an employee of Clean Kin Cleaning Services.
                              </label>
                            </div>
                            
                            <div className="flex items-start space-x-3">
                              <Checkbox
                                id="tax-responsibility"
                                checked={certForm.confirm_tax_responsibility}
                                onCheckedChange={(checked) => 
                                  setCertForm(prev => ({ ...prev, confirm_tax_responsibility: checked }))
                                }
                                data-testid="cert-tax-responsibility"
                              />
                              <label htmlFor="tax-responsibility" className="text-sm text-amber-900 leading-tight cursor-pointer">
                                I understand and accept that I am solely responsible for registering with HMRC, filing Self Assessment tax returns, and paying all Income Tax and National Insurance contributions on my earnings.
                              </label>
                            </div>
                          </div>
                        </div>

                        <Button
                          type="submit"
                          className="w-full bg-[#2D5016] hover:bg-[#1A2E12] text-white rounded-full py-5"
                          disabled={generatingCert || !certForm.confirm_self_employed || !certForm.confirm_tax_responsibility}
                          data-testid="generate-certificate-btn"
                        >
                          {generatingCert ? (
                            <>
                              <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                              Generating...
                            </>
                          ) : (
                            <>
                              <Award className="mr-2 w-4 h-4" />
                              Generate Partnership Certificate
                            </>
                          )}
                        </Button>
                      </form>
                    )}
                  </TabsContent>
                </Tabs>
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-sm text-[#5C6B57] mt-6">
          <Link to="/" className="text-[#2D5016] hover:underline flex items-center justify-center gap-2">
            <ArrowLeft className="w-4 h-4" /> Back to website
          </Link>
        </p>

        {/* First-login onboarding wizard (password + DBS + partnership cert). */}
        <FirstLoginWizard
          open={wizardOpen}
          onOpenChange={setWizardOpen}
          onSwitchTab={(tabId) => setActiveTab(tabId)}
          onPasswordChanged={fetchMe}
        />

        {/* Change password dialog — required after first login with a temp pw. */}
        <Dialog open={changePwOpen} onOpenChange={setChangePwOpen}>
          <DialogContent data-testid="change-password-modal">
            <DialogHeader>
              <DialogTitle>Choose a new password</DialogTitle>
              <DialogDescription>
                Set at least 8 characters. This replaces the temporary password shared with you by the office.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleChangePassword} className="space-y-3">
              <div>
                <Label className="text-xs text-[#5C6B57]">Current password</Label>
                <Input
                  type="password"
                  value={pwForm.current_password}
                  onChange={(e) => setPwForm((p) => ({ ...p, current_password: e.target.value }))}
                  required
                  className="mt-1"
                  data-testid="current-password"
                />
              </div>
              <div>
                <Label className="text-xs text-[#5C6B57]">New password (min. 8 chars)</Label>
                <Input
                  type="password"
                  value={pwForm.new_password}
                  onChange={(e) => setPwForm((p) => ({ ...p, new_password: e.target.value }))}
                  required
                  className="mt-1"
                  data-testid="new-password"
                />
              </div>
              <div>
                <Label className="text-xs text-[#5C6B57]">Confirm new password</Label>
                <Input
                  type="password"
                  value={pwForm.confirm}
                  onChange={(e) => setPwForm((p) => ({ ...p, confirm: e.target.value }))}
                  required
                  className="mt-1"
                  data-testid="confirm-new-password"
                />
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setChangePwOpen(false)}
                  disabled={changingPw}
                  data-testid="cancel-change-password"
                >
                  Later
                </Button>
                <Button
                  type="submit"
                  disabled={changingPw}
                  className="bg-[#2D5016] hover:bg-[#1A2E12] text-white"
                  data-testid="submit-change-password"
                >
                  {changingPw ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Updating…</>
                  ) : (
                    "Update password"
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </motion.div>
    </div>
  );
};

export default CleanerPortal;
